import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';

const CHECK_INTERVAL_MS = 10 * 60 * 1000;

async function fetchVersion(): Promise<string | null> {
  try {
    const { data } = await api.GET('/meta', { cache: 'no-store' });
    return data ? `${data.version}+${data.commit ?? ''}` : null;
  } catch {
    return null; // offline: no hint
  }
}

/**
 * True once the server runs a different version than the one this page loaded
 * with. Checked when the app returns to the foreground and every 10 minutes.
 */
export function useUpdateAvailable(): boolean {
  const [available, setAvailable] = useState(false);
  const loaded = useRef<string | null>(null);

  useEffect(() => {
    let active = true;
    const check = async () => {
      const current = await fetchVersion();
      if (!active || current === null) return;
      if (loaded.current === null) loaded.current = current;
      else if (loaded.current !== current) setAvailable(true);
    };
    void check();
    const timer = window.setInterval(() => void check(), CHECK_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      active = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return available;
}
